import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, NavigationEnd} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {OAuthProvider, SAMLAuthProvider} from 'firebase/auth';
import {Authentication} from '../common/extension/authentication.extension';
import {AccountApiService} from '../common/services/api/routes/account-api.service';
import {AuthGuardService} from '../common/services/auth-guard.service';
import {GoogleAnalyticsService} from '../common/services/google-analytics.service';
import {ThemeService} from '../common/services/theme.service';
import {ThemeId} from '../common/enums/theme-id';
import {LoginError} from '../common/enums/login-error.enum';
import {CommonUtil} from '../common/utils/common.util';
import {Logger} from '../common/utils/logger';
import {Language} from '../../../projects/common/src/lib/enums/language.enum';
import {SSO_PROVIDER_ID_PATTERN, SSO_PROVIDER_NOT_FOUND, SsoApiService, SsoProviderInfo} from './sso-api.service';

export enum LoginUiType {
  login,
  register,
  forgot_password,
  verify_account,
  /** Signing in through the organisation's identity provider (SAML or OIDC). */
  login_saml,
  /** Locked out: removing the second factor with a recovery code. */
  recovery,
  /** Password accepted, waiting on the authenticator-app code. */
  two_factor,
  /** Typing the company sign-in code, for someone who arrived without the link. */
  sso
}

type FieldName = 'email' | 'password' | 'fullName' | 'repeatPassword' | 'twoFactorCode' | 'recoveryCode' | 'ssoCode';

/** Same wait the server applies between two emails of the same kind. */
const RESEND_COOLDOWN_S = 60;
/** Same rule as changing the password in the app. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * The sign-in page: sign in, create an account, reset a password, verify the
 * email, the second factor and its recovery, and company sign-in (SSO).
 *
 * Every screen is its own block in the template, so switching screens swaps
 * the whole form (and animates it) rather than showing and hiding fields.
 * The calls to Firebase and the API are the ones the page always made; the
 * story side (the world being measured) is LoginSceneComponent.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent extends Authentication implements OnInit, AfterViewInit {
  private authGuard = inject(AuthGuardService);
  private accountApi = inject(AccountApiService);
  private ssoApi = inject(SsoApiService);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private themeService = inject(ThemeService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);

  uiTypes = LoginUiType;
  uiType = LoginUiType.login;

  form = {
    fullName: '',
    company: '',
    email: '',
    password: '',
    repeatPassword: ''
  };

  /** A problem with the form as a whole (wrong password, network), already translated. */
  note = '';
  /** Per-field problems, as string keys. */
  errors: Partial<Record<FieldName, string>> = {};
  /** A message waiting on the sign-in screen, e.g. an SSO link that did not work. */
  notice = '';

  working = false;
  /** Which button shows the spinner while `working`. */
  busyAction: 'form' | 'google' | 'sso' | null = null;
  showPassword = false;
  capsLock = false;

  readonly languageList = Object.values(Language) as Language[];
  readonly year = new Date().getFullYear();

  private redirectUrl = '/';
  private intervals = new Set<any>();

  constructor() {
    super();

    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.googleAnalyticsService.log('set', 'page', event.urlAfterRedirects);
        this.googleAnalyticsService.log('send', 'pageview');
      }
    });

    const user = this.auth.currentUser;
    if (user && user.isAnonymous) {
      this.signOut();
    } else if (user && !user.emailVerified) {
      this.sendVerificationEmail();
      this.uiType = LoginUiType.verify_account;
    }

    this.localization.getObservable().pipe(takeUntilDestroyed()).subscribe(() => this.updateTitle());
    this.destroyRef.onDestroy(() => {
      this.intervals.forEach(id => clearInterval(id));
      this.intervals.clear();
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.redirectUrl = LoginComponent.safeRedirect(params.rdt);
      if (params.uit && this.uiType !== LoginUiType.verify_account) {
        switch (params.uit) {
          case '0':
            this.setUiType(LoginUiType.login, false);
            break;
          case '1':
            this.setUiType(LoginUiType.register, false);
            break;
          case '2':
            this.setUiType(LoginUiType.forgot_password, false);
            break;
        }
      }
      // `?saml=` is the link organisations already hand out; `?sso=` says the
      // same for OIDC providers too.
      const ssoId = params.sso || params.saml;
      if (ssoId && this.uiType !== LoginUiType.verify_account) {
        this.lookupSso(String(ssoId), 'link');
      }
      this.updateTitle();
    });
  }

  ngAfterViewInit(): void {
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    // Straight into the first field -- but not on a phone, where focusing
    // throws the keyboard over the page before it has been seen.
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
      this.focusField();
    }
  }

  // ── Screens ──────────────────────────────────────────────────────

  setUiType(type: LoginUiType, focus = true) {
    this.uiType = type;
    this.note = '';
    this.notice = '';
    this.errors = {};
    this.showPassword = false;
    this.capsLock = false;
    if (type === LoginUiType.forgot_password) {
      this.resetSent = false;
    }
    this.updateTitle();
    if (focus) {
      this.focusField();
    }
  }

  /** Browser tab title: what this screen is for, then the product. */
  private updateTitle() {
    const page = this.getString(this.uiType === LoginUiType.register ? 'login_create_account_button' : 'login_sign_in_button');
    const app = this.getString('app_name');
    if (page) {
      this.titleService.setTitle(app ? `${page} · ${app}` : page);
    }
  }

  // ── Sign in ──────────────────────────────────────────────────────

  submitSignIn() {
    if (this.working) {
      return;
    }
    const email = this.form.email.trim();
    this.errors = {};
    this.checkEmail(email);
    if (!this.form.password) {
      this.errors.password = 'login_error_password_required';
    }
    if (this.failedValidation()) {
      return;
    }
    this.begin('form');
    this.auth.loginWithEmail(email, this.form.password).then(() => {
      this.googleAnalyticsService.logEvent('access', 'login_with_email');
      this.onLoginSuccess();
    }).catch(error => {
      if (this.handledAsTwoFactor(error)) { return; }
      this.end();
      this.fail(this.getLoginErrorText(error));
    });
  }

  signInWithGoogle() {
    if (this.working) {
      return;
    }
    this.begin('google');
    this.auth.loginWithGoogle().then(userCredentials => {
      if (userCredentials.user) {
        this.googleAnalyticsService.logEvent('access', 'signin_with_google');
        if (userCredentials.user.emailVerified) {
          this.onLoginSuccess();
        } else {
          this.end();
          this.sendVerificationEmail();
          this.setUiType(LoginUiType.verify_account);
        }
      } else {
        this.end();
      }
    }).catch(error => {
      if (this.handledAsTwoFactor(error)) { return; }
      this.end();
      // Closing Google's window is a change of mind, not something to report.
      if (error !== LoginError.POPUP_CLOSED) {
        this.fail(this.getLoginErrorText(error));
      }
    });
  }

  // ── Create an account ────────────────────────────────────────────

  submitRegister() {
    if (this.working) {
      return;
    }
    const fullName = this.form.fullName.trim();
    const email = this.form.email.trim();
    this.errors = {};
    if (!fullName) {
      this.errors.fullName = 'login_error_name_required';
    }
    this.checkEmail(email);
    if (this.form.password.length < MIN_PASSWORD_LENGTH) {
      this.errors.password = 'password_min_length';
    }
    if (this.form.repeatPassword !== this.form.password) {
      this.errors.repeatPassword = 'passwords_do_not_match';
    }
    if (this.failedValidation()) {
      return;
    }
    this.begin('form');
    this.auth.registerWithEmail(fullName, this.form.company.trim(), email, this.form.password).then(userCredentials => {
      if (userCredentials.user.emailVerified) {
        this.googleAnalyticsService.logEvent('access', 'register_with_email');
        this.onLoginSuccess();
      } else {
        this.end();
        this.sendVerificationEmail();
        this.setUiType(LoginUiType.verify_account);
      }
    }).catch(error => {
      this.end();
      this.fail(this.getLoginErrorText(error));
    });
  }

  /** 0 = nothing typed, 1 weak .. 4 strong. Length first, then variety. */
  get passwordScore(): number {
    const p = this.form.password || '';
    if (!p) {
      return 0;
    }
    if (p.length < MIN_PASSWORD_LENGTH) {
      return 1;
    }
    const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(re => re.test(p)).length;
    return Math.min(4, 1 + (kinds >= 2 ? 1 : 0) + (kinds >= 3 ? 1 : 0) + (p.length >= 12 && kinds >= 2 ? 1 : 0));
  }

  get passwordScoreKey(): string {
    return ['', 'login_strength_weak', 'login_strength_fair', 'login_strength_good', 'login_strength_strong'][this.passwordScore];
  }

  /** The mismatch shows as soon as the second password is as long as the first. */
  get repeatMismatch(): boolean {
    const repeat = this.form.repeatPassword;
    return !!repeat && repeat.length >= this.form.password.length && repeat !== this.form.password;
  }

  // ── Forgot password ──────────────────────────────────────────────

  resetSent = false;
  resetCooldown = 0;
  resendDone = false;

  submitReset() {
    if (this.working || this.resetCooldown > 0) {
      return;
    }
    const email = this.form.email.trim();
    this.errors = {};
    this.checkEmail(email);
    if (this.failedValidation()) {
      return;
    }
    this.sendReset(email, false);
  }

  resendReset() {
    if (this.working || this.resetCooldown > 0) {
      return;
    }
    this.sendReset(this.form.email.trim(), true);
  }

  private sendReset(email: string, again: boolean) {
    this.begin('form');
    this.resendDone = false;
    this.auth.sendPasswordResetEmail(email).then(() => {
      this.googleAnalyticsService.logEvent('access', 'reset_password');
      this.end();
      this.resetSent = true;
      this.resendDone = again;
      this.startCooldown('reset');
      if (!again) {
        this.focusField();
      }
    }).catch(() => {
      this.end();
      this.fail(this.getString('error_reset_pass'));
    });
  }

  // ── Verify the email ─────────────────────────────────────────────

  canVerifyEmail = true;
  verifyCooldown = 0;
  verifySent = false;

  /** The address the verification link went to. */
  get verifyEmailAddress(): string {
    return this.auth.currentUser?.email || this.form.email;
  }

  sendVerificationEmail() {
    const user = this.auth.currentUser;
    if (!user || !this.canVerifyEmail) {
      return;
    }
    this.canVerifyEmail = false;
    // Our own email, in the reader's language, instead of Firebase's stock one.
    this.accountApi.sendEmailVerification(this.language).then(result => {
      this.verifySent = true;
      this.startCooldown('verify');
      // Verified in the meantime (another tab, another device): carry on.
      if (result?.verified) {
        this.checkVerified();
      }
    }).catch(() => {
      this.canVerifyEmail = true;
    });
  }

  /**
   * "I've verified my email". The link opens in another tab, so this one
   * never learns about it by itself and used to stay on this screen for good.
   */
  checkVerified() {
    const user = this.auth.currentUser;
    if (!user || this.working) {
      return;
    }
    this.begin('form');
    user.reload().then(async () => {
      const fresh = this.auth.currentUser;
      if (fresh?.emailVerified) {
        // A fresh token carries the verified claim the server checks.
        await fresh.getIdToken(true).catch(() => null);
        this.onLoginSuccess();
        return;
      }
      this.end();
      this.fail(this.getString('login_not_verified_yet'));
    }).catch(err => {
      Logger.error('Reloading the user failed', err);
      this.end();
      this.fail(this.getString('login_error_network'));
    });
  }

  // ── Second factor ────────────────────────────────────────────────
  // Reached from every sign-in path — email, Google, SAML — because a second
  // factor is a property of the account, not of how you signed in.

  twoFactorCode = '';
  verifyingTwoFactor = false;

  get twoFactorFactorName(): string {
    return this.auth.pendingMfaFactorName;
  }

  /** Names the enrolled factor when Firebase gave it a label. */
  get twoFactorPrompt(): string {
    const name = this.twoFactorFactorName;
    return name
      ? this.getString('two_factor_prompt_named', name)
      : this.getString('two_factor_prompt');
  }

  /**
   * True when the error is the second-factor challenge rather than a failure.
   * Switches the form to the code prompt instead of printing an error.
   */
  private handledAsTwoFactor(error: any): boolean {
    if (error !== LoginError.MFA_REQUIRED) {
      return false;
    }
    this.end();
    this.twoFactorCode = '';
    this.setUiType(LoginUiType.two_factor);
    return true;
  }

  /** Digits only, and the code goes as soon as all six are in (typed or pasted). */
  onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 6);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.twoFactorCode = digits;
    delete this.errors.twoFactorCode;
    if (digits.length === 6 && !this.verifyingTwoFactor) {
      this.verifyTwoFactorCode();
    }
  }

  verifyTwoFactorCode() {
    if (this.verifyingTwoFactor) {
      return;
    }
    const code = (this.twoFactorCode || '').trim();
    this.errors = {};
    // Every TOTP authenticator emits six digits; catching it here saves a
    // round trip and a rate-limit hit on a typo.
    if (!/^\d{6}$/.test(code)) {
      this.errors.twoFactorCode = 'login_error_code_six_digits';
      this.failedValidation();
      return;
    }
    this.verifyingTwoFactor = true;
    this.note = '';
    this.auth.completeTotpSignIn(code).then(() => {
      this.googleAnalyticsService.logEvent('access', 'login_with_second_factor');
      this.onLoginSuccess();
    }).catch(err => {
      Logger.error('Second-factor sign-in failed', err);
      this.verifyingTwoFactor = false;
      this.fail(err?.code === 'auth/invalid-verification-code' || err?.code === 'auth/invalid-credential'
        ? this.getString('invalid_verification_code')
        : this.getString('error_unknown_contact'));
      // Clear it for the next try; the field keeps the focus.
      this.twoFactorCode = '';
      this.focusField();
    });
  }

  /** Back out of the code prompt without leaving a half-finished sign-in. */
  cancelTwoFactor() {
    this.auth.cancelMfaChallenge();
    this.twoFactorCode = '';
    this.setUiType(LoginUiType.login);
  }

  // ── Recovery ─────────────────────────────────────────────────────
  // The way back in when the authenticator is gone. Firebase has no notion of
  // backup codes, so this does not complete the sign-in — it removes the
  // second factor entirely, after checking the password and spending a code,
  // and the user then signs in normally.

  recoveryCode = '';
  recoveringTwoFactor = false;

  startRecovery() {
    this.recoveryCode = '';
    this.setUiType(LoginUiType.recovery);
  }

  cancelRecovery() {
    this.recoveryCode = '';
    // Back to the code prompt, not the login form: the password was already
    // accepted, and sending them back to retype it would be a step backwards.
    this.setUiType(this.auth.hasPendingMfaChallenge ? LoginUiType.two_factor : LoginUiType.login);
  }

  submitRecovery() {
    if (this.recoveringTwoFactor) {
      return;
    }
    const email = (this.form.email || '').trim();
    const code = (this.recoveryCode || '').trim();
    this.errors = {};
    if (!code) {
      this.errors.recoveryCode = 'login_error_recovery_code_required';
    }
    if (this.failedValidation()) {
      return;
    }
    if (!email || !this.form.password) {
      // Only reachable after a password was accepted; without one there is
      // nothing to check the code against, so start again from the top.
      this.setUiType(LoginUiType.login);
      this.fail(this.getString('error_fill_fields'));
      return;
    }
    this.recoveringTwoFactor = true;
    this.note = '';
    this.accountApi.recoverTwoFactor(email, this.form.password, code).then(() => {
      this.recoveringTwoFactor = false;
      this.auth.cancelMfaChallenge();
      this.recoveryCode = '';
      // The factor is gone but the session was never established, and the
      // server revoked the tokens as part of recovery. Signing in again from
      // the normal form is the only correct next step.
      this.setUiType(LoginUiType.login);
      this.showSnackBar(this.getString('recovery_success'), 10000);
    }).catch(err => {
      this.recoveringTwoFactor = false;
      const code2 = err?.msg?.errorCode;
      this.fail(typeof code2 === 'string' && this.strings[code2]
        ? this.getString(code2)
        : this.getString('error_unknown_contact'));
      Logger.error('Two-factor recovery failed', err);
    });
  }

  // ── Company sign-in (SSO) ────────────────────────────────────────
  // An organisation hands its people a link, /login?saml=<provider id>. The
  // server says whether that provider belongs to an active organisation and
  // what the organisation is called; the sign-in itself is Firebase's popup.

  ssoCode = '';
  ssoLoading = false;
  ssoProvider: SsoProviderInfo | null = null;
  private ssoAuthProvider: SAMLAuthProvider | OAuthProvider | null = null;

  startSso() {
    this.ssoCode = '';
    this.setUiType(LoginUiType.sso);
  }

  submitSsoCode() {
    if (this.ssoLoading) {
      return;
    }
    this.errors = {};
    const raw = (this.ssoCode || '').trim();
    const providerId = LoginComponent.providerIdFrom(raw);
    if (!raw) {
      this.errors.ssoCode = 'login_sso_code_required';
    } else if (!providerId) {
      this.errors.ssoCode = 'login_sso_code_invalid';
    }
    if (this.failedValidation()) {
      return;
    }
    this.lookupSso(providerId, 'code');
  }

  private lookupSso(providerId: string, source: 'link' | 'code') {
    const id = (providerId || '').trim();
    if (!SSO_PROVIDER_ID_PATTERN.test(id)) {
      this.ssoLinkFailed('login_sso_unknown', source);
      return;
    }
    this.ssoLoading = true;
    this.ssoProvider = null;
    this.ssoAuthProvider = null;
    this.note = '';
    if (source === 'link') {
      // The organisation's screen, in its loading state, straight away.
      this.setUiType(LoginUiType.login_saml, false);
    }
    this.ssoApi.getProvider(id).then(info => {
      this.ssoLoading = false;
      const providerIdFound = info?.providerId || id;
      const type = info?.type === 'oidc' || providerIdFound.startsWith('oidc.') ? 'oidc' : 'saml';
      this.ssoProvider = {providerId: providerIdFound, type, name: (info?.name || '').trim() || providerIdFound};
      this.ssoAuthProvider = type === 'oidc' ? new OAuthProvider(providerIdFound) : new SAMLAuthProvider(providerIdFound);
      this.setUiType(LoginUiType.login_saml);
    }).catch(err => {
      this.ssoLoading = false;
      const notFound = err?.code === 404 || err?.msg?.errorCode === SSO_PROVIDER_NOT_FOUND;
      Logger.error('SSO provider lookup failed', err);
      this.ssoLinkFailed(notFound ? 'login_sso_unknown' : 'login_sso_lookup_failed', source);
    });
  }

  /**
   * A link that names no organisation used to drop silently to the normal
   * form, leaving the person wondering why their company button was gone.
   */
  private ssoLinkFailed(key: string, source: 'link' | 'code') {
    if (source === 'code') {
      this.fail(this.getString(key));
      return;
    }
    this.setUiType(LoginUiType.login, false);
    this.notice = this.getString(key);
  }

  signInWithSAML() {
    if (!this.ssoAuthProvider || this.working) {
      return;
    }
    this.begin('sso');
    this.auth.signInWithProvider(this.ssoAuthProvider, this.ssoProvider?.name || '').then(userCredentials => {
      if (userCredentials.user) {
        const providerId = userCredentials.user.providerId;
        this.googleAnalyticsService.logEvent('access', 'signin_with_' + providerId);
        if (userCredentials.user.emailVerified) {
          this.onLoginSuccess();
        } else {
          this.end();
          this.sendVerificationEmail();
          this.setUiType(LoginUiType.verify_account);
        }
      } else {
        this.end();
      }
    }).catch(error => {
      if (this.handledAsTwoFactor(error)) { return; }
      this.end();
      if (error !== LoginError.POPUP_CLOSED) {
        this.fail(this.getLoginErrorText(error));
      }
    });
  }

  /** A provider id typed as is, or taken out of a pasted sign-in link. */
  static providerIdFrom(raw: string): string {
    let value = (raw || '').trim();
    if (/^https?:\/\//i.test(value) || value.includes('?')) {
      try {
        const url = new URL(value, window.location.origin);
        value = url.searchParams.get('sso') || url.searchParams.get('saml') || '';
      } catch {
        return '';
      }
    }
    return SSO_PROVIDER_ID_PATTERN.test(value) ? value : '';
  }

  // ── Page tools ───────────────────────────────────────────────────

  get isDark(): boolean {
    return document.body.classList.contains('dark-theme');
  }

  toggleTheme() {
    this.themeService.setTheme(this.isDark ? ThemeId.LIGHT : ThemeId.DARK);
  }

  /**
   * Straight to the strings service: nobody is signed in here, so there is
   * no account to save the choice to (the browser keeps it, as in the app).
   */
  changeLanguage(value: string) {
    const lang = this.languageList.find(l => l === value);
    if (lang && lang !== this.language) {
      this.localization.setLanguage(lang);
    }
  }

  // ── Fields ───────────────────────────────────────────────────────

  clearError(field: FieldName) {
    if (this.errors[field]) {
      delete this.errors[field];
    }
  }

  onPasswordKey(event: KeyboardEvent) {
    if (typeof event.getModifierState === 'function') {
      this.capsLock = event.getModifierState('CapsLock');
    }
  }

  /**
   * A translated sentence cut at its ${n} placeholders, so a value (an email
   * address, a link) sits in it as its own element and never as HTML.
   */
  split(key: string): string[] {
    return (this.strings[key] || '').split(/\$\{\d\}/);
  }

  private checkEmail(email: string) {
    if (!email) {
      this.errors.email = 'login_error_email_required';
    } else if (!CommonUtil.validateEmail(email)) {
      this.errors.email = 'login_error_email_invalid';
    }
  }

  /** On a validation problem: shake, and put the cursor in the first bad field. */
  private failedValidation(): boolean {
    const first = Object.keys(this.errors)[0] as FieldName;
    if (!first) {
      return false;
    }
    this.note = '';
    this.shake();
    this.focusField(first);
    return true;
  }

  private fail(message: string) {
    this.note = message;
    this.shake();
  }

  private begin(action: 'form' | 'google' | 'sso') {
    this.working = true;
    this.busyAction = action;
    this.note = '';
    this.notice = '';
  }

  private end() {
    this.working = false;
    this.busyAction = null;
  }

  /** The screen currently shown (the one leaving is still in the DOM for a moment). */
  private activeScreen(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.screen:not(.screen-out)');
  }

  /** Focus a field by name, or the screen's first field (or its heading) after it renders. */
  private focusField(field?: FieldName) {
    setTimeout(() => {
      const screen = this.activeScreen();
      const target = screen?.querySelector<HTMLElement>(field ? `[data-field="${field}"]` : '[data-autofocus]');
      target?.focus();
    });
  }

  private shake() {
    const target = this.activeScreen()?.querySelector('form') ?? this.activeScreen();
    if (!target || typeof target.animate !== 'function'
      || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      return;
    }
    target.animate([
      {transform: 'translateX(0)'},
      {transform: 'translateX(-7px)'},
      {transform: 'translateX(6px)'},
      {transform: 'translateX(-4px)'},
      {transform: 'translateX(2px)'},
      {transform: 'translateX(0)'},
    ], {duration: 380, easing: 'cubic-bezier(0.36, 0.07, 0.19, 0.97)'});
  }

  private startCooldown(which: 'reset' | 'verify') {
    const set = (value: number) => which === 'reset' ? this.resetCooldown = value : this.verifyCooldown = value;
    set(RESEND_COOLDOWN_S);
    let left = RESEND_COOLDOWN_S;
    const id = setInterval(() => {
      left = Math.max(0, left - 1);
      set(left);
      if (left === 0) {
        clearInterval(id);
        this.intervals.delete(id);
        if (which === 'verify') {
          this.canVerifyEmail = true;
        }
      }
    }, 1000);
    this.intervals.add(id);
  }

  getLoginErrorText(error: LoginError): string {
    switch (error) {
      case LoginError.INVALID_PASSWORD:
      case LoginError.INVALID_EMAIL:
        return this.getString('login_error_wrong_credentials');
      case LoginError.USER_NOT_FOUND:
        return this.getString('login_error_no_account');
      case LoginError.USER_DISABLED:
        return this.getString('login_error_user_disabled');
      case LoginError.NETWORK_REQUEST_FAILED:
        return this.getString('login_error_network');
      case LoginError.TOO_MANY_REQUESTS:
        return this.getString('login_error_too_many_requests');
      case LoginError.EMAIL_ALREADY_IN_USE:
        return this.getString('login_error_email_in_use');
      case LoginError.WEAK_PASSWORD:
        return this.getString('weak_password');
      case LoginError.OPERATION_NOT_ALLOWED:
        return this.getString('login_error_method_off');
      case LoginError.EXISTS_WITH_DIFFERENT_CREDENTIAL:
        return this.getString('login_error_different_method');
      case LoginError.POPUP_BLOCKED:
        return this.getString('login_error_popup_blocked');
      default:
        return this.getString('error_unknown_contact');
    }
  }

  onLoginSuccess() {
    window.location.href = this.redirectUrl;
  }

  /**
   * Where to go after signing in, from `?rdt=`. Only a path on this site: the
   * value used to go straight into `location.href`, so a crafted link could
   * send someone who had just signed in to another site, or run a
   * `javascript:` URL in the app. Every real caller passes a router path.
   */
  static safeRedirect(raw: any): string {
    if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')
      || /[\u0000-\u001F\u007F\\]/.test(raw)) {
      return '/';
    }
    try {
      const url = new URL(raw, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search + url.hash : '/';
    } catch {
      return '/';
    }
  }
}
